import { Button, IconButton } from "@mui/material";
import React, { useState, useRef, useCallback, useEffect } from "react";
import QuestionDragReorder from "./QuestionDragReorder";

// question types
import MultipleChoice from "./types/MultipleChoice";
import TrueFalse from "./types/TrueFalse";
import DragDropPair from "./types/DragDropPair";
import Sorting from "./types/Sorting";
import ShortAnswer from "./types/ShortAnswer";

// icons
import { RiSave3Fill } from "react-icons/ri";
import { ReactComponent as LeftChevron } from "../../../assets/chevron-left.svg";
import { ReactComponent as RightChevron } from "../../../assets/chevron-right.svg";
import { ReactComponent as AddQuestion } from "../../../assets/addQuestion.svg";
import { ReactComponent as DeleteQuestion } from "../../../assets/deleteQuestion.svg";

// question type icons
import { ReactComponent as DragDropPairIcon } from "./icons/quiz_dragDropPair.svg";
import { ReactComponent as DragWordsIcon } from "./icons/quiz_dragWords.svg";
import { ReactComponent as MarkWordsIcon } from "./icons/quiz_markWords.svg";
import { ReactComponent as MultipleChoiceIcon } from "./icons/quiz_multipleChoice.svg";
import { ReactComponent as SortingIcon } from "./icons/quiz_sorting.svg";
import { ReactComponent as TrueFalseIcon } from "./icons/quiz_trueFalse.svg";
import { ReactComponent as ShortAnswerIcon } from "./icons/quiz_shortAnswer.svg";
// import { ReactComponent as MatchGame } from "./icons/quiz_matchGame.svg";

import "./quizModule.css";

function QuizModule({ quiz, setQuiz, lang, bookId }) {
  const [localQuestions, setLocalQuestions] = useState(
    quiz?.questions?.slice() || []
  );
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [choosingType, setChoosingType] = useState(false);
  // const [quizSounds, setQuizSounds] = useState({});
  const questionTypeRef = useRef();
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  const [feedbackTiming, setFeedbackTiming] = useState("IMMEDIATE");

  const MAX_QUIZ_LENGTH = 100;

  useEffect(() => {
    if (choosingType) {
      setTimeout(() => window.addEventListener("click", checkClickOut), 50);
    } else {
      window.removeEventListener("click", checkClickOut);
    }
  }, [choosingType]);

  const checkClickOut = useCallback((e) => {
    if (!questionTypeRef.current.contains(e.target)) {
      setChoosingType(false);
    }
  }, []);

  function createQuestion(type) {
    window.removeEventListener("click", checkClickOut);
    setLocalQuestions([
      ...localQuestions,
      {
        id: localQuestions.length,
        type: type,
        contents: {
          q: { en: "" },
          a: [],
          media: [],
        },
      },
    ]);
    setCurrentQuestion(localQuestions.length); // intended to be 1 behind, as it references an index
    setChoosingType(false);
  }

  function updateQuestion({
    id,
    type,
    contents,
    shuffleAnswers,
    shuffleA,
    shuffleB,
    feedback,
  }) {
    let temp = localQuestions.slice();
    temp = temp.map((mappedQuestion) => {
      if (mappedQuestion.id === id) {
        return {
          id,
          type,
          contents,
          ...(shuffleAnswers && { shuffleAnswers }),
          ...(shuffleA && { shuffleA }),
          ...(shuffleB && { shuffleB }),
          ...(feedback && { feedback }),
        };
      } else return mappedQuestion;
    });
    setLocalQuestions(temp);
  }

  function deleteQuestion() {
    let temp = localQuestions.slice();
    temp = temp.filter((q) => q.id !== currentQuestion);
    reorderIds(temp);
    setCurrentQuestion(() => Math.max(currentQuestion - 1, 0));
    setLocalQuestions(temp);
  }

  function changeScoreVisibillity(e) {
    let temp = { ...quiz };
    temp.scoreVisible = e.target.checked;
    setQuiz(temp);
  }
  function changeFeedBackTiming(feedbackTiming) {
    let temp = { ...quiz };
    temp.feedbackTiming = feedbackTiming;
    setQuiz(temp);
  }
  function changeAttemptCount(e) {
    let temp = { ...quiz };
    temp.attempts = parseInt(e.target.value);
    setQuiz(temp);
  }

  function changeSkippability(e) {
    let temp = { ...quiz };
    temp.skippable = e.target.checked;
    setQuiz(temp);
  }

  function reorderIds(array) {
    array.forEach((q, i) => (q.id = i));
  }

  function handleSaveQuiz() {
    setQuiz({
      ...quiz,
      // sfx: {
      //   ...quizSoundy,
      // },
      questions: localQuestions,
    });

    // animate save indicator
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
    }, 1250); // the timeout duration needs to match the duration of the instantFadeOut animation in quizModule.css
  }
  const handleMediaUpdate = (
    questionIndex,
    answerIndex,
    mediaUrl,
    mediaType
  ) => {
    const updatedQuestions = [...localQuestions];
    if (answerIndex !== null) {
      const answerMedia =
        updatedQuestions[questionIndex].contents.a[answerIndex].media;
      const mediaIndex = answerMedia.findIndex(
        (media) => media.language === lang.code
      );

      if (mediaIndex > -1) {
        // Replace existing media
        answerMedia[mediaIndex] = {
          ...answerMedia[mediaIndex],
          type: mediaType,
          url: mediaUrl,
          language: lang.code,
        };
      } else {
        // Push new media
        answerMedia.push({
          type: mediaType,
          url: mediaUrl,
          language: lang.code,
        });
      }
    } else {
      console.log("thisia the updated quesiton");
      console.log(updatedQuestions[questionIndex]);

      const questionMedia = updatedQuestions[questionIndex].contents.media;
      const mediaIndex = questionMedia.findIndex(
        (media) => media.language === lang.code
      );

      if (mediaIndex > -1) {
        // if index found
        // Replace existing media
        questionMedia[mediaIndex] = {
          ...questionMedia[mediaIndex],
          type: mediaType,
          url: mediaUrl,
          language: lang.code,
        };
      } else {
        // Push new media
        questionMedia.push({
          type: mediaType,
          url: mediaUrl,
          language: lang.code,
        });
      }
    }
    setLocalQuestions(updatedQuestions);
  };

  return (
    <div className="popup__quizMaker">
      <nav className="QM__navbar">
        <div>
          <Button
            className="QM__back-button"
            color="blackButton"
            variant="outlined"
            size="small"
            onClick={() => setShowSettings((s) => !s)}
          >
            {showSettings ? (
              <>
                <LeftChevron />
                Back
              </>
            ) : (
              "Quiz settings"
            )}
          </Button>
          <IconButton
            color="blueButton"
            size="small"
            onClick={handleSaveQuiz}
            text={"Save quiz"}
          >
            <RiSave3Fill size={"1.5em"} />
          </IconButton>
          <span
            className={`QM__quiz-save-indicator ${
              showSaveIndicator ? "fadeOut" : ""
            }`}
          >
            Saved
          </span>
        </div>
        {showSettings && (
          <>
            <p className="QM-settings__main-label">Quiz settings</p>
            <div style={{ width: "5rem" }} />
          </>
        )}
        {localQuestions.length > 1 && !showSettings && (
          <div className="QM__quiz-navigator">
            <button
              onClick={() => setCurrentQuestion((c) => Math.max(0, c - 1))}
              disabled={currentQuestion === 0}
            >
              <LeftChevron />
            </button>
            <p>{currentQuestion + 1}</p>
            <button
              onClick={() =>
                setCurrentQuestion((c) =>
                  Math.min(c + 1, localQuestions.length)
                )
              }
              disabled={currentQuestion + 1 === localQuestions.length}
            >
              <RightChevron />
            </button>
          </div>
        )}
        {!showSettings && (
          <div className="QM__question-type-container">
            <button
              className="question-management-button"
              id="add"
              disabled={localQuestions.length >= MAX_QUIZ_LENGTH}
              onClick={() => setChoosingType(true)}
            >
              <AddQuestion />
            </button>
            <button
              className="question-management-button"
              id="delete"
              disabled={localQuestions.length < 2}
              onClick={deleteQuestion}
            >
              <DeleteQuestion />
            </button>
            {choosingType && (
              <div ref={questionTypeRef} className="QM__type-chooser">
                <div>
                  <button onClick={() => createQuestion("multipleChoice")}>
                    <MultipleChoiceIcon />
                  </button>
                  <p className="buttonLabel">Multiple choice</p>
                </div>
                <div>
                  <button onClick={() => createQuestion("trueFalse")}>
                    <TrueFalseIcon />
                  </button>
                  <p className="buttonLabel">True / false</p>
                </div>
                <div>
                  <button onClick={() => createQuestion("dragDropPair")}>
                    <DragDropPairIcon />
                  </button>
                  <p className="buttonLabel">Drag & drop pairs</p>
                </div>
                <div>
                  <button onClick={() => createQuestion("sorting")}>
                    <SortingIcon />
                  </button>
                  <p className="buttonLabel">Sorting</p>
                </div>
                <div>
                  <button onClick={() => createQuestion("dragWords")}>
                    <DragWordsIcon />
                  </button>
                  <p className="buttonLabel">Drag words</p>
                </div>
                <div>
                  <button onClick={() => createQuestion("markWords")}>
                    <MarkWordsIcon />
                  </button>
                  <p className="buttonLabel">Mark words</p>
                </div>
                <div>
                  <button onClick={() => createQuestion("shortAnswer")}>
                    <ShortAnswerIcon />
                  </button>
                  <p className="buttonLabel">Short answer</p>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>
      {showSettings ? (
        <div className="QM__contents" id="settings">
          <div
            className={`QM-settings__option-container ${
              localQuestions.length <= 1 ? "disabled" : ""
            }`}
          >
            <p className="QM__setting-label">
              Question order{" "}
              {localQuestions.length < 2
                ? "(requires at least two questions)"
                : ""}
            </p>
            <QuestionDragReorder
              questions={localQuestions}
              setQuestions={setLocalQuestions}
            />
          </div>
          <div className="QM-settings__option-container">
            <p className="QM__setting-label">Sound effects</p>
            <label>
              Correct answer: <input type="text" />
            </label>
            <label>
              Incorrect answer: <input type="text" />
            </label>
          </div>
          <div className="QM-settings__option-container">
            <p className="QM__setting-label">Misc options</p>
            <label>
              Score visible?{" "}
              <input
                type="checkbox"
                checked={quiz.scoreVisible}
                onChange={changeScoreVisibillity}
              />
            </label>
            <label>
              Feedback Timing:
              <select
                value={feedbackTiming}
                onChange={(e) => changeFeedBackTiming(e.target.value)}
              >
                <option value="IMMEDIATE">After Each Question</option>
                <option value="AFTER_QUIZ">At the End</option>
              </select>
            </label>
            <label>
              Skippable questions?{" "}
              <input
                type="checkbox"
                checked={quiz.skippable}
                onChange={changeSkippability}
              />
            </label>
            <label>
              Number of attempts per question:{" "}
              <input
                type="number"
                value={quiz.attempts ?? 0}
                min={0}
                max={3}
                onChange={changeAttemptCount}
              />
            </label>
          </div>
        </div>
      ) : (
        <div className="QM__contents">
          {localQuestions.length > 0 && (
            <QuestionSwitcher
              bookId={bookId}
              handleMediaUpdate={handleMediaUpdate}
              item={localQuestions[currentQuestion]}
              updateItem={updateQuestion}
              lang={lang}
            />
          )}
        </div>
      )}
    </div>
  );
}

function QuestionSwitcher({
  item,
  updateItem,
  lang,
  handleMediaUpdate,
  bookId,
}) {
  function findType() {
    switch (item.type) {
      // case "dragWords": {
      //   return <React.Fragment key={item.id}>dragWords</React.Fragment>;
      // }
      // case "markWords": {
      //   return <React.Fragment key={item.id}>markWords</React.Fragment>;
      // }
      case "dragDropPair": {
        return (
          <React.Fragment key={item.id}>
            <DragDropPair
              item={item}
              bookId={bookId}
              updateItem={updateItem}
              lang={lang}
              handleMediaUpdate={handleMediaUpdate}
            />
          </React.Fragment>
        );
      }
      // case "matchGame": {
      //   return <React.Fragment key={item.id}>matchGame</React.Fragment>;
      // }
      case "multipleChoice": {
        return (
          <React.Fragment key={item.id}>
            <MultipleChoice
              bookId={bookId}
              item={item}
              updateItem={updateItem}
              lang={lang}
              handleMediaUpdate={handleMediaUpdate}
            />
          </React.Fragment>
        );
      }
      case "sorting": {
        return (
          <React.Fragment key={item.id}>
            <Sorting item={item} updateItem={updateItem} lang={lang} />
          </React.Fragment>
        );
      }
      case "trueFalse": {
        return (
          <React.Fragment key={item.id}>
            <TrueFalse item={item} updateItem={updateItem} lang={lang} />
          </React.Fragment>
        );
      }
      case "shortAnswer": {
        return (
          <React.Fragment key={item.id}>
            <ShortAnswer
              item={item}
              updateItem={updateItem}
              lang={lang}
              bookId={bookId}
            />
          </React.Fragment>
        );
      }
      default: {
        return <div>Error: No type!</div>;
      }
    }
  }

  return <>{findType()}</>;
}

export default QuizModule;
